/**
 * Calculate great-circle distance between two WGS84 coordinates using the Haversine formula.
 * @param location1 位置1
 * @param location2 位置2
 * @param unit Output unit: 'm' (meters), 'km', 'mi' (miles), 'nmi' (nautical miles). Default 'm'.
 * @returns Distance between the two points in the specified unit.
 */
export function haversineDistance(
  location1: { latitude: number; longitude: number },
  location2: { latitude: number; longitude: number },
  unit: 'm' | 'km' | 'mi' | 'nmi' = 'm',
): number {
  // Mean Earth radius per IUGG (meters)
  // const R = 6371008.8;
  // MySQLのデフォルトに設定している
  const R = 6_370_986

  // convert degrees to radians
  const toRad = (deg: number) => (deg * Math.PI) / 180

  const phi1 = toRad(location1.latitude)
  const phi2 = toRad(location2.latitude)
  const deltaPhi = toRad(location2.latitude - location1.latitude)
  const deltaLambda = toRad(location2.longitude - location1.longitude)

  const sinDeltaPhi2 = Math.sin(deltaPhi / 2)
  const sinDeltaLambda2 = Math.sin(deltaLambda / 2)

  const a = sinDeltaPhi2 * sinDeltaPhi2 + Math.cos(phi1) * Math.cos(phi2) * sinDeltaLambda2 * sinDeltaLambda2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const meters = R * c

  switch (unit) {
    case 'km':
      return meters / 1000
    case 'mi':
      return meters / 1609.344
    case 'nmi':
      return meters / 1852
    default:
      return meters
  }
}

/**
 * バックエンドの計算方法と合わせた距離計算
 * Degree-based planar distance scaled by 100,000.
 * Treats (lon, lat) as 2D Cartesian coords in degrees and returns
 * hypot(Δlat, Δlon) * 100000.
 *
 * ⚠ Not a geodesic distance. It's just a scaled degree distance.
 * @returns number  — “meters風”の値（厳密なメートルではありません）
 */
export const degreeDistanceTimes100k = (
  location1: { latitude: number; longitude: number },
  location2: { latitude: number; longitude: number },
): number => {
  const dLat = location2.latitude - location1.latitude
  const dLon = location2.longitude - location1.longitude
  return Math.hypot(dLat, dLon) * 100_000
}
